import { ArrowRight } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { DocumentIcon, FileOutlineIcon, JobsIcon } from "../../assets/icons/interface/interface";
import Drawer from "../../components/Drawer/Drawer";
import { actions } from "../../handlers/enums";
import { capitalizeFirstLetterOfWords } from "../../handlers/utilityFunctions";
import useIsLoggedIn from "../../hooks/use-IsLoggedIn";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import { jobTypes } from "../../seed/job-preferences-data";
import { useResumes, useSmartProfile, useUserAccount } from "../../services/create-resume/queries";
import { ResumeDataService } from "../../services/create-resume/resume-data";
import BulkFitCheckFlow from "../FitCheck/components/BulkFitCheckFlow";
import FitCheckBasketIcon from "../FitCheck/components/FitCheckBasketIcon";
import { Button } from "../JobsPage/components/Button";
import Loader from "../StaticPages/HomePage/components/Loader";
import ViewJob from "../StaticPages/HomePage/components/ViewJob";
import DocumentCenter from "./components/DocumentCenter";
import DSPItem from "./components/DSPItem";
import FloatingNav from "./components/FloatingNav";
import GmailConnectAccess from "./components/GmailConnectAccess";
import Header from "./components/Header";
import JobPreferencesModal from "./components/JobPreferencesModal";
import { Modal } from "./components/Modal";
import OutlookConnectAccess from "./components/OutlookConnectAccess";
import ProfileSummaryCard from "./components/ProfileSummaryCard";
import RecommendedJobs from "./components/RecommendedJobs";
import SavedJobs from "./components/SavedJobs";
import SmartProfileCard from "./components/SmartProfileCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/Tabs";
import UploadResumeModal from "./components/UploadResumeModal";

/**
 * Older accounts come back with openForWorkTypes as a comma-joined string instead of an array. Left
 * alone the buttons still light up — String.includes matches substrings — and saving echoes the
 * string back to an endpoint that validates it as an enum array, which rejects the whole request
 * with "each value in openForWorkTypes must be one of the following values". Coerce to an array and
 * drop anything the API would refuse.
 */
function normalizeWorkTypes(value) {
	const entries = Array.isArray(value) ? value : typeof value === "string" ? value.split(",") : [];

	return entries.map((entry) => String(entry).trim()).filter((entry) => jobTypes.includes(entry));
}

function transformToRequestBody(currentPayload, personalInfo) {
	return {
		emailAddress: personalInfo.emailAddress,
		firstName: personalInfo.firstName,
		middleName: personalInfo.middleName,
		lastName: personalInfo.lastName,
		phoneNumber: personalInfo.phoneNumber,
		jobPreferences: {
			primaryResumeReferenceId: currentPayload.primaryResumeReferenceId,
			currentJobsearchStatus: currentPayload.currentJobsearchStatus,
			openForWorkTypes: normalizeWorkTypes(currentPayload.openForWorkTypes),
			areasOfExpertise: currentPayload.areasOfExpertise,
			seniorityLevel: currentPayload.seniorityLevel,
			locationPreferenceType: currentPayload.locationPreferenceType,
			locationsPreferred: currentPayload.locationsPreferred,
			recentGraduate: currentPayload.recentGraduate,
			graduationDate: {
				month: currentPayload.graduationDate?.month,
				year: currentPayload.graduationDate?.year,
			},
			professionalSince: {
				month: currentPayload.professionalSince?.month,
				year: currentPayload.professionalSince?.year,
			},
			educationLevels: currentPayload.educationLevels,
			desiredTitle: currentPayload.desiredTitle,
			address: {
				city: currentPayload?.address?.city,
				// Kept alongside the city: the location picker stores "District,Region", so dropping the
				// region would leave the saved value unable to match an option on the way back in.
				region: currentPayload?.address?.region,
				country: "Tanzania",
			},
			skills: currentPayload.skills,
		},
	};
}

/** Long enough for the preferences modal to unmount and release its `body { overflow: hidden }` lock. */
const MODAL_CLOSE_MS = 250;

function Profile() {
	const showDSP = useFeatureFlag("dsp");
	const showOCA = useFeatureFlag("oneClickApply");

	const isLoggedIn = useIsLoggedIn();
	const dispatch = useDispatch();
	const resumeDataService = useMemo(() => new ResumeDataService(), []);

	const [isScrolled, setIsScrolled] = useState(false);
	const [showJobDetailsModal, setShowJobDetailsModal] = useState(false);
	const [uploadResumeModal, setUploadResumeModal] = useState(false);
	const [selectedJob, setSelectedJob] = useState(null);
	const [selectedDSPId, setSelectedDSPId] = useState(null);
	const [showFitCheckBasket, setShowFitCheckBasket] = useState(false);

	const [showPreferencesModal, setShowPreferencesModal] = useState(false);
	// Controlled rather than uncontrolled so that saving preferences can pull the user back to the
	// recommended list — see scrollToJobPicks.
	const [jobsTab, setJobsTab] = useState<string | null>(null);
	// The store has no RootState type yet, so the reducer map isn't inferable here.
	const dspProfileState = useSelector((state: any) => state.ProfileDSPReducer);

	const { data: resumeList, isLoading: isLoadingResumes } = useResumes();

	const [showDocument, setShowDocument] = useState(false);
	const [documentName, setDocumentName] = useState("");
	const [documentUrl, setDocumentUrl] = useState(null);
	const [loadingDocument, setLoadingDocument] = useState(true);

	useEffect(() => {
		if (isLoadingResumes) return;
	}, [resumeList, isLoadingResumes]);

	const [jobPreferences, setJobPreferences] = useState({});

	const { data: accountData, isLoading: isLoadingAccount, refetch } = useUserAccount();
	const { data: smartProfileData, isLoading: isLoadingSmartProfileData } = useSmartProfile();

	// smartProfileData is undefined while the query loads, undefined when the query is
	// disabled because there is no auth cookie, and empty for a user who has no Smart
	// Profile yet. Object.keys throws on the first two, which white-screens the page.
	const hasSmartProfile = !!smartProfileData && Object.keys(smartProfileData).length > 0;

	const preferencesProgress = accountData?.job_preference_progress_status;
	// Titles are cut from an uploaded file name, so some carry trailing whitespace.
	const dspTitles = useMemo(
		() =>
			((smartProfileData?.dsps ?? []) as Array<{ dsp_title?: string }>)
				.map((dsp) => dsp.dsp_title?.trim())
				.filter((title): title is string => Boolean(title)),
		[smartProfileData],
	);
	const resumeCount = Array.isArray(resumeList) ? resumeList.length : 0;

	// Only the sections that actually render. Preferences are no longer a scrollable section — they
	// live in the sidebar, which is always on screen — so the rail would have scrolled nowhere.
	const navItems = useMemo(
		() =>
			[
				{ id: "job-recommendations", label: "Job picks", icon: <JobsIcon className="w-5 h-5" /> },
				hasSmartProfile && { id: "documents", label: "Documents", icon: <DocumentIcon className="w-5 h-5" /> },
			].filter(Boolean),
		[hasSmartProfile],
	);

	// Always open on Recommended. It used to fall back to Saved Jobs when no areas of expertise were
	// set, which landed a brand-new account on "No saved jobs yet" — a dead end. Recommended's empty
	// state says why there is nothing yet and offers the fix.
	const activeJobsTab = jobsTab ?? "recommended";

	/**
	 * Saving re-scores the recommendations, so take the user to the refreshed list instead of leaving
	 * them wherever they opened the modal from. The wait covers the modal close: while it is mounted it
	 * holds the body scroll lock, which swallows the scroll.
	 */
	const scrollToJobPicks = () => {
		setJobsTab("recommended");
		setTimeout(() => {
			document.getElementById("job-recommendations")?.scrollIntoView({ behavior: "smooth", block: "start" });
		}, MODAL_CLOSE_MS);
	};

	useEffect(() => {
		if (isLoadingAccount) return;
		setJobPreferences(
			accountData?.jobPreferences && {
				...accountData.jobPreferences,
				openForWorkTypes: normalizeWorkTypes(accountData.jobPreferences.openForWorkTypes),
			},
		);
		// Incomplete preferences are surfaced by the upsell banner above rather than by force-opening
		// this modal over the page.
	}, [accountData, isLoadingAccount]);

	const onChangeJobStatus = (status) => {
		setJobPreferences({
			...jobPreferences,
			currentJobsearchStatus: status,
		});
		resumeDataService.jobPreferences(
			transformToRequestBody(jobPreferences, {
				emailAddress: accountData?.email,
				firstName: accountData?.firstName,
				middleName: accountData?.middleName,
				lastName: accountData?.lastName,
				phoneNumber: accountData?.phoneNumber,
			}),
			(data) => {
				refetch();
				localStorage.setItem("jobPreferences", JSON.stringify(data?.data));
			},
			(error) => {
				const errorMessages = error.response.data.message;
				toast.error(errorMessages);
			},
		);
	};

	useEffect(() => {
		const handleScroll = () => {
			setIsScrolled(window.scrollY > 0);
		};

		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	useEffect(() => {
		const payload = {
			fromDSP: false,
			actionPerformed: "",
		};

		if (dspProfileState.fromDSP && dspProfileState.actionPerformed === actions.CREATE) {
			setUploadResumeModal(true);
		}

		if (dspProfileState.fromDSP && dspProfileState.actionPerformed === actions.UPDATE) {
			const element = document.getElementById("documents");
			if (element) element.scrollIntoView({ behavior: "smooth" });
		}

		dispatch({ type: "RESET_DSP_STATE", payload });
	}, []);

	if (isLoadingResumes || isLoadingSmartProfileData) {
		return <div className="w-full h-screen bg-white flex items-center justify-center">Loading...</div>;
	}

	return (
		<>
			<Drawer
				isOpen={showDocument}
				onClose={() => {
					setDocumentUrl(null);
					setDocumentName("");
					setLoadingDocument(true);
					setShowDocument(false);
				}}
			>
				{showDocument && documentUrl && (
					<div>
						{loadingDocument && (
							<div className="h-[calc(88vh)] flex flex-col items-center justify-center">
								<Loader />
							</div>
						)}
						{!loadingDocument && (
							<div className="flex items-center py-[16px] gap-[8px]">
								<FileOutlineIcon className="w-[18px] h-[18px]" />
								<h6 className="text-[14px] font-semibold">{documentName}</h6>
								<Button
									variant="outline"
									className="ml-auto"
									onClick={() => {
										const link = document.createElement("a");
										link.href = documentUrl;
										link.download = documentName || "document";
										link.target = "_blank";
										document.body.appendChild(link);
										link.click();
										document.body.removeChild(link);
									}}
								>
									Download
								</Button>
							</div>
						)}
						<iframe
							src={documentUrl}
							className="w-full h-[calc(88vh)] rounded-lg border border-gray-100"
							title="Document Preview"
							onLoad={() => {
								setLoadingDocument(false);
							}}
						/>
					</div>
				)}
			</Drawer>
			<div className={`sticky top-0 z-50 bg-white transition-all duration-300 ${isScrolled ? "shadow-xs" : ""}`}>
				<Header isLoggedIn={isLoggedIn} variant="applicant" onChangeJobStatus={onChangeJobStatus} />
			</div>

			<Drawer
				isOpen={showJobDetailsModal}
				onClose={() => {
					setShowJobDetailsModal(false);
				}}
			>
				<ViewJob data={selectedJob} setData={setSelectedJob} setShowJobDetailsModal={setShowJobDetailsModal} />
			</Drawer>
			<Modal
				isOpen={uploadResumeModal}
				onClose={() => {
					setUploadResumeModal(false);
				}}
				title="Get noticed. Get Hired – Upload your resume!"
				size="xl"
			>
				<UploadResumeModal onClose={() => setUploadResumeModal(false)} />
			</Modal>

			{/* Shell matches the FitCheck dashboard — grey ground, white surfaces, max-w-5xl — so this
			    page reads as the same product as the rest of the signed-in app. */}
			<div className="min-h-screen bg-gray-50">
				<div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
					<FloatingNav items={navItems} />

					{/* No page subtitle. The one that used to sit here described the job list, which on a
					    phone is now below the profile card, so it read as a caption for the wrong thing. It
					    moved to the picks heading. */}
					<h1 className="mb-8 text-3xl font-bold text-gray-900 font-plus-jakarta">
						Welcome back{accountData?.firstName ? `, ${capitalizeFirstLetterOfWords(accountData.firstName)}` : ""}
					</h1>

					{/* Job Preferences Modal */}
					<JobPreferencesModal
						isOpen={showPreferencesModal}
						onClose={() => {
							setShowPreferencesModal(false);
							refetch();
						}}
						setJobPreferences={setJobPreferences}
						jobPreferences={jobPreferences}
						resumeList={resumeList}
						transformToRequestBody={transformToRequestBody}
						onSaved={scrollToJobPicks}
					/>

					{/* Source order puts the sidebar first so it leads on a phone, where the profile card
					    collapses to its header and strength meter. `order` flips it back to the right on
					    desktop. Documents and the email upsell stay outside this block so they land below
					    the sidebar on a phone rather than above it. */}
					<div className="flex flex-col lg:flex-row gap-8">
						<aside className="order-1 lg:order-2 lg:w-1/3 space-y-6">
							<ProfileSummaryCard
								firstName={accountData?.firstName}
								lastName={accountData?.lastName}
								picture={accountData?.picture}
								jobPreferences={accountData?.jobPreferences}
								completenessDetails={preferencesProgress?.completeness_details}
								completenessPct={preferencesProgress?.completeness_pct ?? 0}
								onEditPreferences={() => setShowPreferencesModal(true)}
							/>
							{/* Only while there is no Smart Profile. Once one exists the DSPs and Documents
							    sections below already show it, and this card repeats them. */}
							{!hasSmartProfile && (
								<SmartProfileCard
									dspTitles={dspTitles}
									resumeCount={resumeCount}
									onUploadResume={() => setUploadResumeModal(true)}
								/>
							)}
						</aside>

						<div className="order-2 lg:order-1 lg:w-2/3">
							<section id="job-recommendations" className="scroll-mt-24">
								<div className="flex items-start justify-between gap-4 mb-4">
									<div>
										<h2 className="text-xl font-bold text-gray-800 font-plus-jakarta">Job picks for you</h2>
										<p className="mt-1 text-sm text-gray-600 font-inter">
											Matched to your preferences and skills, rescored whenever you change them.
										</p>
									</div>
									{/* Picks are a shortlist. Search, filters and the full board live on /jobs. */}
									<Link
										to="/jobs"
										className="group shrink-0 inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:underline underline-offset-4"
									>
										See all jobs
										<ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 motion-reduce:transition-none" />
									</Link>
								</div>

								<Tabs value={activeJobsTab} onValueChange={setJobsTab} className="w-full">
									<TabsList className="flex border-b-0">
										<TabsTrigger
											value="recommended"
											className="ignore-focus-behavior rounded-none text-sm text-gray-700 mr-6"
										>
											Recommended
										</TabsTrigger>
										<TabsTrigger value="saved" className="ignore-focus-behavior rounded-none text-sm text-gray-700">
											Saved Jobs
										</TabsTrigger>
									</TabsList>

									<TabsContent value="recommended">
										<div className="mt-4 bg-white rounded-lg border border-gray-200 px-5">
											<RecommendedJobs
												setShowPreferencesModal={setShowPreferencesModal}
												setSelectedJob={setSelectedJob}
												setShowJobDetailsModal={setShowJobDetailsModal}
											/>
										</div>
									</TabsContent>

									<TabsContent value="saved">
										<div className="mt-4 bg-white rounded-lg border border-gray-200 px-5">
											<SavedJobs setSelectedJob={setSelectedJob} setShowJobDetailsModal={setShowJobDetailsModal} />
										</div>
									</TabsContent>
								</Tabs>
							</section>
						</div>
					</div>

					<div className="mt-8 space-y-8">
						{/* Connect Email Upsell */}
						{showOCA && (
							<div className="bg-blue-50/50 rounded-lg p-6 border gap-4 border-blue-100 h-full flex flex-col md:flex-row items-center">
								<img
									alt="Complete your profile"
									loading="lazy"
									decoding="async"
									data-nimg="fill"
									className="object-contain h-full w-full left-0 top-0 right-0 bottom-0 max-w-[200px]"
									src="/images/job-applicant.png"
								/>
								<div className="flex flex-col md:flex-row items-center gap-8 md:gap-4">
									<div className="space-y-1">
										<h2 className="text-lg font-semibold tracking-normal text-gray-900 mt-1 mb-2">
											Send Application From Your Own Inbox
										</h2>
										<p className="text-gray-800 text-sm mb-8">
											Make your application stand out by sending them directly from your Gmail-not just a platform. With
											one, employers get a personal email from you, not Dproz. Replies go straight to your inbox.
										</p>
									</div>
									<GmailConnectAccess />
									<OutlookConnectAccess />
								</div>
							</div>
						)}

						{showDSP && hasSmartProfile && (
							<section className="bg-white rounded-lg border border-gray-200 p-6">
								<DSPItem
									dspList={smartProfileData ? smartProfileData.dsps : []}
									setDSPId={(value) => setSelectedDSPId(value)}
									smartProfile={smartProfileData}
								/>
							</section>
						)}

						{/* Documents Section */}
						{hasSmartProfile && (
							<section id="documents" className="scroll-mt-24 bg-white rounded-lg border border-gray-200 p-6">
								{/* smartProfile is not a prop — DocumentCenter calls useSmartProfile() itself. */}
								<DocumentCenter
									selectedDSPId={selectedDSPId}
									setShowDocument={setShowDocument}
									setDocumentName={setDocumentName}
									setDocumentUrl={setDocumentUrl}
									setLoadingDocument={setLoadingDocument}
								/>
							</section>
						)}
					</div>
				</div>
			</div>

			{/* FitCheck Basket Floating Icon */}
			<div className="fixed bottom-6 right-6 z-40">
				<FitCheckBasketIcon onClick={() => setShowFitCheckBasket(true)} />
			</div>

			{/* FitCheck Basket Drawer */}
			<BulkFitCheckFlow isOpen={showFitCheckBasket} onClose={() => setShowFitCheckBasket(false)} />
		</>
	);
}

export default Profile;
